import { useRef, useState, useEffect, useCallback } from 'react';
import { useI18n } from 'context/i18n';
import { Pressable } from 'react-aria-components';
import useEmblaCarousel from 'embla-carousel-react';
import Fade from 'embla-carousel-fade';
import Autoplay from 'embla-carousel-autoplay';

import { Button } from 'components/ui/button';
import { ChevronSVG } from 'components/iconsSVG/chevron';
import { LabChevronDownIcon } from 'components/camperlab/icons/chevron-down';
import { ChevronLeftSVG } from 'components/iconsSVG/chevronLeft';
import { LabChevronLeftIcon } from 'components/camperlab/icons/chevron-left';
import { LabChevronRightIcon } from 'components/camperlab/icons/chevron-right';
import { TouchTarget } from 'components/ui/touch-target';
import { Link } from 'components/ui/link';
import type { AutoString } from 'types/utils';
import { getDomainValue, isDomain } from 'utils/domain';
import { useEventListener } from 'hooks/useEventListener';

function ExpandIcon(props: React.SVGProps<SVGSVGElement>) {
  return getDomainValue({ camper: <ChevronSVG {...props} />, nnormal: <ChevronSVG {...props} />, camperlab: <LabChevronDownIcon {...props} /> });
}

function LeftIcon(props: React.SVGProps<SVGSVGElement>) {
  return getDomainValue({ camper: <ChevronLeftSVG {...props} />, nnormal: <ChevronLeftSVG {...props} />, camperlab: <LabChevronLeftIcon {...props} /> });
}

function RightIcon(props: React.SVGProps<SVGSVGElement>) {
  return getDomainValue({
    camper: <ChevronLeftSVG {...props} className={`rotate-180 ${props.className ?? ''}`} />,
    nnormal: <ChevronLeftSVG {...props} className={`rotate-180 ${props.className ?? ''}`} />,
    camperlab: <LabChevronRightIcon {...props} />,
  });
}

type RibbonProps = {
  items: { anchorId: string; title: string; type: AutoString<'DETAIL' | 'TEXT' | 'NEWSLETTER'>; detail: string | null; nl: boolean }[];
  onClickNewsletter: () => void;
  accountWidget: React.ReactNode;
  camperlab: boolean;
};

export function Ribbon({ items = [], onClickNewsletter, accountWidget, camperlab }: RibbonProps) {
  const { t, locale } = useI18n();
  const ref = useRef<HTMLDivElement | null>(null);
  const [expandedContent, setExpandedContent] = useState<string | null>(null);
  const expanded = expandedContent !== null;

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, watchDrag: false, active: !expanded }, [
    Autoplay({ active: !expanded, delay: 4000, stopOnMouseEnter: true, stopOnInteraction: false }),
    Fade(),
  ]);

  const expandedContentId = 'expanded-content';
  const io = useRef<IntersectionObserver | null>(null);

  const handleOnClickRibbon = (content: RibbonProps['items'][number]) => {
    if (content.type === 'NEWSLETTER') {
      onClickNewsletter();
    }
  };

  const handleOnClickExpand = (content: RibbonProps['items'][number]) => {
    setExpandedContent((prev) => (prev === null ? content.detail : null));
  };

  const onContinue = (next = true) => {
    if (expandedContent !== null) setExpandedContent(null);
    if (!emblaApi) return;
    // timeout porque si no el carousel esta desactivado en ese render. en el siguiente render ya esta activado
    setTimeout(() => {
      if (next) {
        emblaApi.scrollNext();
      } else {
        emblaApi.scrollPrev();
      }
    });
  };

  const ribbonRefCallback = useCallback((node: HTMLDivElement) => {
    //add an event listener for scroll that updates a data property on the node data-whow
    if (node !== null) {
      ref.current = node;
      const ribbonHeight = node.offsetHeight;
      document.body.style.setProperty('--ribbon-height', `${ribbonHeight}px`);
      io.current = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            node.dataset.show = 'true';
          } else {
            node.dataset.show = 'false';
            setExpandedContent(null);
          }
        });
      });
      io.current.observe(node);
    }
  }, []);

  useEffect(() => {
    // TODO: en react 19 mover al callback
    return () => {
      if (io.current) {
        io.current.disconnect();
      }
    };
  }, []);

  useEventListener(
    'resize',
    () => {
      if (ref.current) {
        const ribbonHeight = ref.current.offsetHeight;
        document.body.style.setProperty('--ribbon-height', `${ribbonHeight}px`);
      }
    },
    undefined,
    { passive: true },
  );

  useEventListener(
    'click',
    (event: Event) => {
      if (ref.current) {
        const eventId = (event?.target as HTMLElement)?.id;
        if (!ref.current.contains(event.target as Node) && eventId !== expandedContentId) {
          setExpandedContent(null);
        }
      }
    },
    undefined,
    { passive: true },
  );

  return (
    <div
      className={`${isDomain('camper') && !camperlab ? '[--bg:var(--color-grey-light)] [--color:theme(textColor.copy)]' : '[--bg:theme(backgroundColor.neutral.inverted)] [--color:theme(textColor.inverted)]'} relative z-[1000]`}
    >
      <div
        ref={ribbonRefCallback}
        className="z-100 relative grid min-h-[34px] grid-cols-1 grid-rows-1 bg-[var(--bg)] text-[var(--color)] transition-transform duration-200 ease-in-out data-[show=false]:-translate-y-full data-[show=true]:translate-y-0 lab:min-h-[20px]"
        id="header-ribbon"
        data-show="true"
      >
        <div className="col-start-1 row-start-1 self-center">
          <div className="mx-auto">
            <div className="overflow-hidden" ref={emblaRef}>
              <div className="flex">
                {Array.isArray(items) && items.length > 0 ?
                  items.map((content, index) => {
                    if (!content || !content.type) return null;
                    const hasContent = content.detail !== null && content.detail !== undefined && content.detail !== '';
                    const showExpandedContent = isDomain('camper', 'nnormal') && content.type !== 'NEWSLETTER' && hasContent;
                    const hasNextPrevButtons = isDomain('camper', 'nnormal') && items.length > 1;
                    return (
                      <div className="transform-[translate3d(0,0,0)] min-w-0 shrink-0 grow-0 basis-full" key={`slide-${index}`}>
                        <div className="flex h-full items-center justify-center gap-2.5 px-2 text-xs lab:justify-start lab:py-px md:justify-start md:px-6 lab:md:px-2.5 lg:justify-center lab:lg:justify-start">
                          {showExpandedContent ?
                            <Button variant="none" className="relative flex items-baseline" onPress={() => handleOnClickExpand(content)}>
                              <TouchTarget className="size-5" />
                              <ExpandIcon data-expanded={expanded} className="size-3 transition-transform data-[expanded=true]:rotate-180" />
                            </Button>
                          : null}
                          <div className="flex items-center gap-1 text-center">
                            {hasNextPrevButtons ?
                              <Button variant="none" className="relative flex h-full items-baseline" onPress={() => onContinue(false)}>
                                <TouchTarget className="size-5" />
                                <LeftIcon className="size-3" />
                              </Button>
                            : null}

                            <Pressable
                              onPress={() => {
                                if (showExpandedContent) {
                                  handleOnClickExpand(content);
                                } else {
                                  handleOnClickRibbon(content);
                                }
                              }}
                            >
                              <div
                                role="button"
                                dangerouslySetInnerHTML={{ __html: content.title }}
                                className="cursor-pointer lab:text-left lab:text-xs lab:uppercase [&_a]:text-[var(--color)] [&_a]:hover:text-[var(--color)]"
                              />
                            </Pressable>
                            {content.detail !== '' && content.type !== 'NEWSLETTER' && isDomain('camperlab') && (
                              <Button
                                variant="none"
                                className={`shrink-0 text-xs uppercase lg:text-xxs [&>span]:underline`}
                                onClick={() => handleOnClickExpand(content)}
                              >
                                {!expanded ? 'More info' : 'Less info'}
                              </Button>
                            )}
                            {hasNextPrevButtons ?
                              <Button variant="none" className="relative flex h-full items-baseline" onPress={() => onContinue(true)}>
                                <TouchTarget className="size-5" />
                                <RightIcon className="size-3" />
                              </Button>
                            : null}
                          </div>
                        </div>
                      </div>
                    );
                  })
                : null}
              </div>
            </div>
          </div>
        </div>
        {getDomainValue({
          camper: (
            <div
              className={`z-10 col-start-1 row-start-1 flex items-center gap-8 self-center justify-self-end whitespace-nowrap bg-[var(--bg)] pr-2 text-xs text-[var(--color)] max-md:hidden md:pr-6 lab:md:pr-2.5`}
            >
              <Link variant="none" href={`/${locale}/shops`} className="uppercase text-inverted hover:text-inverted camper:text-copy camper:hover:text-copy">
                {t('generico', 'tiendas')}
              </Link>
              <Button variant="none" onPress={onClickNewsletter} className="cursor-pointer uppercase text-[var(--color)]">
                {t('newsletter', 'join.us')}
              </Button>
              {accountWidget}
            </div>
          ),
          nnormal: 'camper',
          camperlab: null,
        })}
      </div>
      {expanded && expandedContent && (
        <div
          id={expandedContentId}
          className="absolute top-[var(--ribbon-height)] z-[200] min-w-full bg-[var(--bg)] p-10 text-center text-xs text-[var(--color)] lab:px-2.5 lab:py-1 lab:pt-10 lab:text-left lab:text-xs lab:uppercase lab:lg:text-xxs [&_a]:text-[var(--color)] [&_a]:hover:text-[var(--color)]"
          dangerouslySetInnerHTML={{ __html: expandedContent }}
        />
      )}
    </div>
  );
}
